/**
 * 水平線角マップ（§5）。実行時レイキャストの代わりに事前計算タイルをO(1)参照する。
 *
 * タイルバイナリ形式（前処理パイプライン tools/terrain と共有）：
 *   header 40B: magic "UVKT" | version u8 | heightMode u8(0=adult,1=stroller) | pad u16 |
 *               originLat f64 | originLon f64 | cellSizeM f64 | cellsX u32 | cellsY u32
 *   body: セルごとに 16方位の水平線角 uint8（0.5°刻み、§5.2）＋ SVF uint8（/255）
 *         行優先（南西原点、x=東向き, y=北向き）
 *   version 2: 各セル末尾に flags uint8 を追加（bit0 = 歩行可、経路探索用）。
 *              v1タイルは全セル歩行可として読む（後方互換）。
 */
import {
  HORIZON_QUANT_DEG,
  HORIZON_SECTORS,
  TERRAIN_CELL_M,
  WX_CELL_DEG,
} from "./constants.js";
import type { LatLon, TerrainCell } from "./types.js";

const MAGIC = 0x554b5654; // "UVKT" big-endian
const HEADER_BYTES = 40;
/** バージョン別のセルバイト数（v1: 水平線16+SVF、v2: +flags） */
const CELL_BYTES: Record<number, number> = {
  1: HORIZON_SECTORS + 1,
  2: HORIZON_SECTORS + 2,
};

/**
 * 中身が地形タイルかどうかを先頭のマジックナンバーで判定する。
 * HTTPステータスだけを信じると、静的配信が未知のパスに index.html を
 * 200で返した場合にHTMLをタイルとして読んでしまう（実際に起きた）。
 */
export function isTerrainTileBuffer(buf: ArrayBuffer | null | undefined): boolean {
  if (!buf || buf.byteLength < HEADER_BYTES) return false;
  return new DataView(buf).getUint32(0) === MAGIC;
}

/** 太陽方位 [deg] → 方位セクタ 0..15（セクタ0は北を中心とする22.5°幅） */
export function sector(azDeg: number): number {
  const a = ((azDeg % 360) + 360) % 360;
  return Math.floor(((a + 11.25) % 360) / 22.5);
}

/** SVF ≈ (1/N) Σ cos²(h_i)（§5.1） */
export function svfFromHorizon(horizonDeg: number[]): number {
  const sum = horizonDeg.reduce((s, h) => s + Math.cos((h * Math.PI) / 180) ** 2, 0);
  return sum / horizonDeg.length;
}

/** 気象セルID：floor(lat/0.05) + "_" + floor(lon/0.05)（§5.5） */
export function wxCellId(pos: LatLon): string {
  return `${Math.floor(pos.lat / WX_CELL_DEG)}_${Math.floor(pos.lon / WX_CELL_DEG)}`;
}

/** 気象セルIDの中心座標（Worker側でOpen-Meteoに渡す座標） */
export function wxCellCenter(cellId: string): LatLon | null {
  const m = /^(-?\d+)_(-?\d+)$/.exec(cellId);
  if (!m) return null;
  return {
    lat: (Number(m[1]) + 0.5) * WX_CELL_DEG,
    lon: (Number(m[2]) + 0.5) * WX_CELL_DEG,
  };
}

export class TerrainTile {
  private readonly cellBytes: number;

  private constructor(
    readonly version: number,
    readonly originLat: number,
    readonly originLon: number,
    readonly cellSizeM: number,
    readonly cellsX: number,
    readonly cellsY: number,
    private readonly data: Uint8Array,
  ) {
    this.cellBytes = CELL_BYTES[version]!;
  }

  static decode(buf: ArrayBuffer): TerrainTile {
    const dv = new DataView(buf);
    if (dv.getUint32(0) !== MAGIC) throw new Error("terrain tile: bad magic");
    const version = dv.getUint8(4);
    const cellBytes = CELL_BYTES[version];
    if (!cellBytes) throw new Error(`terrain tile: unsupported version ${version}`);
    const originLat = dv.getFloat64(8);
    const originLon = dv.getFloat64(16);
    const cellSizeM = dv.getFloat64(24);
    const cellsX = dv.getUint32(32);
    const cellsY = dv.getUint32(36);
    const expected = HEADER_BYTES + cellsX * cellsY * cellBytes;
    if (buf.byteLength < expected) throw new Error("terrain tile: truncated");
    return new TerrainTile(
      version,
      originLat,
      originLon,
      cellSizeM,
      cellsX,
      cellsY,
      new Uint8Array(buf, HEADER_BYTES),
    );
  }

  private mPerDegLon(): number {
    return 111320 * Math.cos((this.originLat * Math.PI) / 180);
  }

  /** 緯度経度 → セル座標。範囲外は null */
  cellIndexOf(pos: LatLon): { x: number; y: number } | null {
    const x = Math.floor(((pos.lon - this.originLon) * this.mPerDegLon()) / this.cellSizeM);
    const y = Math.floor(((pos.lat - this.originLat) * 111320) / this.cellSizeM);
    if (x < 0 || y < 0 || x >= this.cellsX || y >= this.cellsY) return null;
    return { x, y };
  }

  /** タイルのカバー範囲（目的地検索のバイアス・範囲外判定に使う） */
  bounds(): { west: number; south: number; east: number; north: number } {
    return {
      west: this.originLon,
      south: this.originLat,
      east: this.originLon + (this.cellsX * this.cellSizeM) / this.mPerDegLon(),
      north: this.originLat + (this.cellsY * this.cellSizeM) / 111320,
    };
  }

  /** セル座標 → セル中心の緯度経度 */
  cellCenter(x: number, y: number): LatLon {
    return {
      lat: this.originLat + ((y + 0.5) * this.cellSizeM) / 111320,
      lon: this.originLon + ((x + 0.5) * this.cellSizeM) / this.mPerDegLon(),
    };
  }

  /** セル座標で参照（経路探索用）。範囲チェックは呼び出し側 */
  cellAt(x: number, y: number): TerrainCell {
    const off = (y * this.cellsX + x) * this.cellBytes;
    const horizonDeg: number[] = new Array(HORIZON_SECTORS);
    for (let i = 0; i < HORIZON_SECTORS; i++) {
      horizonDeg[i] = this.data[off + i]! * HORIZON_QUANT_DEG;
    }
    return {
      horizonDeg,
      svf: this.data[off + HORIZON_SECTORS]! / 255,
      // v1タイルはflagsを持たないため全セル歩行可として扱う
      walkable: this.version >= 2 ? (this.data[off + HORIZON_SECTORS + 1]! & 1) === 1 : true,
    };
  }

  /** タイル範囲内なら地形セル値、外なら null */
  lookup(pos: LatLon): TerrainCell | null {
    const idx = this.cellIndexOf(pos);
    return idx ? this.cellAt(idx.x, idx.y) : null;
  }
}

/**
 * タイル未取得・範囲外のフォールバック：開けた場所（水平線角0・SVF1）とみなす。
 * 遮蔽を無視する＝被曝を多めに見積もる側＝安全側（§1.2）。
 */
export const OPEN_SKY_CELL: TerrainCell = {
  horizonDeg: new Array(HORIZON_SECTORS).fill(0),
  svf: 1,
};

export { TERRAIN_CELL_M };
